import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { describeError } from "../utils/error_message";
import { errorStatus } from "./auth_api";
import { authErrorKey, validateCode, type AuthStep } from "./auth_rules";
import { AuthField, PRIMARY_BUTTON_CLASS } from "./auth_fields";

const BACK_CLASS = [
	"h-12 rounded-md border border-auth-line bg-auth-card text-auth-text text-base",
	"cursor-pointer hover:border-auth-sub transition-colors",
].join(" ");

interface Props {
	email: string;
	/** Which request checks the code — registration or password recovery. */
	step: Extract<AuthStep, "register" | "verify-reset">;
	verify: (code: string) => Promise<void>;
	onBack: () => void;
}

/** The six-digit code from the e-mail, shared by registration and password recovery. */
const CodeForm = ({ email, step, verify, onBack }: Props) => {
	const { t } = useTranslation();
	const [code, setCode] = useState("");
	const [error, setError] = useState<string | undefined>();
	const [loading, setLoading] = useState(false);

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		if (loading) return;
		const found = validateCode(code);
		setError(found.code);
		if (found.code) return;

		setLoading(true);
		try {
			await verify(code);
		} catch (err) {
			console.error("Landing code check failed", describeError(err));
			setError(authErrorKey(step, errorStatus(err)).key);
			setLoading(false);
		}
	};

	return (
		<form className="flex flex-col gap-3" onSubmit={submit} noValidate>
			<p className="m-0 text-[15px] leading-[1.5] text-auth-text">
				{t("auth.code-sent", { email })}
			</p>
			<p className="m-0 text-[13px] leading-[1.45] text-auth-sub">
				{t("auth-page.auth-code.text")}
			</p>
			<AuthField
				inputMode="numeric"
				autoComplete="one-time-code"
				maxLength={6}
				placeholder={t("auth-page.auth-code.input-placeholder")}
				value={code}
				error={error}
				onChange={e => {
					setCode(e.target.value);
					setError(undefined);
				}}
			/>
			<div className="grid grid-cols-[1fr_2fr] gap-3">
				<button type="button" className={BACK_CLASS} onClick={onBack}>
					{t("auth-page.auth-code.buttons.back")}
				</button>
				<button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={loading}>
					{t(loading ? "auth-page.auth-code.buttons.loading" : "auth.code-confirm")}
				</button>
			</div>
		</form>
	);
};

export default CodeForm;
